import { useUncontrolledError } from "../../utils/useUncontrolledError";
import MappingValueInput from "./MappingValueInput";
import EnhancedIntercomFieldValueMappingInput from "./intercom/EnhancedIntercomFieldValueMappingInput";
import EnhancedAsanaRemoteFieldInput from "./merge/EnhancedAsanaRemoteFieldInput";
import EnhancedLinearStatusFields from "./merge/EnhancedLinearStatusFields";
import EnhancedMergeUserMappingInput from "./merge/EnhancedMergeUserMappingInput";
import EnhancedNotionFieldValueMappingInput from "./notion/EnhancedNotionFieldValueMappingInput";
import EnhancedZendeskMappingInput from "./zendesk/EnhancedZendeskMappingInput";
import { getBuiltInFields, getBuiltInFieldsClientMapping, getFullFieldName, mappingFieldIsValid } from "@avenueops/frontend-shared";
import { fieldIsRequired } from "@avenueops/frontend-shared/dist/mappings/mapping-utils/fieldIsRequired";
import {
  ClientMappingApiType,
  ClientMappingField,
  ENHANCED_MERGE_USER_FIELDS,
  ENHANCED_ZENDESK_FIELDS,
  MERGE_NOTIFICATION_TYPES,
  MERGE_STATUS,
  ENHANCED_INTERCOM_FIELDS,
  MERGE_ASANA_REMOTE_FIELDS,
  ENHANCED_NOTION_FIELDS,
} from "@avenueops/shared-types";
import _ from "lodash";
import React from "react";
import { useFormContext } from "react-hook-form";

interface Props {
  update: (_: ClientMappingField) => void;
  selectedIntegrationId: string;
  integrationData: any;
  field: ClientMappingField;
  mappingPrefix: string;
  clientMappingApiType: ClientMappingApiType;
  isFirst: boolean;
  prefix: string;
  notificationPrefix?: string;
}

export function EnhancedMappingInput({
  selectedIntegrationId,
  integrationData,
  update,
  mappingPrefix,
  clientMappingApiType,
  isFirst,
  field,
  prefix,
  notificationPrefix,
}: Props) {
  const { watch } = useFormContext();
  const fullFieldName = getFullFieldName(mappingPrefix, field?.name);
  const recipientValue = notificationPrefix ? watch(`${notificationPrefix}.recipientValue`) : undefined;
  const error = useUncontrolledError<ClientMappingField>(mappingFieldIsValid, field);

  if (field.type !== "VALUE") return null;

  if (clientMappingApiType === "ZENDESK" && field && ENHANCED_ZENDESK_FIELDS.has(fullFieldName)) {
    return (
      <EnhancedZendeskMappingInput
        zendeskIntegrationId={selectedIntegrationId}
        fieldError={error}
        fullFieldName={fullFieldName}
        fieldValue={field.value}
        onChange={(newValue: string) => update({ ...field, value: newValue })}
        mappingPrefix={mappingPrefix}
        isFirst={isFirst}
      />
    );
  }

  if (MERGE_NOTIFICATION_TYPES.has(clientMappingApiType) && ENHANCED_MERGE_USER_FIELDS.has(fullFieldName)) {
    return (
      <EnhancedMergeUserMappingInput
        integrationId={selectedIntegrationId}
        integrationData={integrationData}
        fieldError={error}
        fieldValue={field.value}
        onChange={(newValue: string) => update({ ...field, value: newValue })}
        isFirst={isFirst}
      />
    );
  }

  if (clientMappingApiType === "ASANA" && fullFieldName.includes(MERGE_ASANA_REMOTE_FIELDS)) {
    return (
      <EnhancedAsanaRemoteFieldInput
        integrationId={selectedIntegrationId}
        fieldError={error}
        fieldValue={field.value}
        onChange={(newValue: string) => update({ ...field, value: newValue })}
        fullFieldName={fullFieldName}
      />
    );
  }

  if (clientMappingApiType === "NOTION" && ENHANCED_NOTION_FIELDS.some((fieldName) => fullFieldName.includes(fieldName))) {
    return (
      <EnhancedNotionFieldValueMappingInput
        integrationId={selectedIntegrationId}
        recipientValue={recipientValue}
        fullFieldName={fullFieldName}
        fieldValue={field.value}
        fieldError={error}
        onChange={(newValue: string) => update({ ...field, value: newValue })}
        isFirst={isFirst}
        update={update}
        field={field}
        prefix={prefix}
      />
    );
  }

  if (clientMappingApiType === "LINEAR" && fullFieldName === MERGE_STATUS) {
    return (
      <EnhancedLinearStatusFields
        integrationId={selectedIntegrationId}
        fullFieldName={fullFieldName}
        recipientValue={recipientValue}
        fieldError={error}
        fieldValue={field.value}
        onChange={(newValue: string) => update({ ...field, value: newValue })}
      />
    );
  }

  if (clientMappingApiType === "INTERCOM" && ENHANCED_INTERCOM_FIELDS.has(fullFieldName)) {
    return (
      <EnhancedIntercomFieldValueMappingInput
        integrationId={selectedIntegrationId}
        fullFieldName={fullFieldName}
        fieldError={error}
        fieldValue={field.value}
        onChange={(newValue: string) => update({ ...field, value: newValue })}
        isFirst={isFirst}
        update={update}
        field={field}
        prefix={prefix}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <MappingValueInput update={update} item={field} isFirst={isFirst} prefix={prefix} />
    </div>
  );
}
